import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { formatToDecimals } from '@lib/helpers/formatting';

export interface Datapoint {
	field: string;
	label: string;
	mainValue: number;
	color: string;
	tooltip: string;
}

const HorizontalBarGraph = ({ data }: { data: Datapoint[] }) => {
	const svgRef = useRef<SVGSVGElement>();
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(800);
	const [hasAnimated, setHasAnimated] = useState(false);

	// Resize observer per la larghezza del contenitore
	useEffect(() => {
		const handleResize = () => {
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};
		handleResize();
		const currentContainer = containerRef.current;
		const observer = new (window as any).ResizeObserver(handleResize);
		if (currentContainer) observer.observe(currentContainer);
		return () => {
			if (currentContainer) observer.unobserve(currentContainer);
		};
	}, []);

	useEffect(() => {
		const dataPoints = data;

		const margin = { top: 20, right: 20, bottom: 80, left: 150 };
		const width = containerWidth - margin.left - margin.right;
		const barHeight = 80;
		const height = dataPoints.length * barHeight;

		const yScale = d3.scaleBand().range([0, height]).padding(0);
		const xScale = d3.scaleLinear().range([0, width - margin.right]);

		// Clear previous svg content before drawing
		d3.select(svgRef.current).selectAll('*').remove();

		const svg = d3
			.select(svgRef.current)
			.attr('width', width + margin.left + margin.right)
			.attr('height', height + margin.top + margin.bottom)
			.append('g')
			.attr('transform', `translate(${margin.left},${margin.top})`);

		yScale.domain(dataPoints.map((d) => d.label));

		const xMax = Math.max(...dataPoints.map((d) => +d.mainValue));
		const xDomainMax = xMax > 0 ? xMax * 1.2 : 1;
		const xDomain = xMax > 0 ? [0, xDomainMax] : [-1, 10];

		xScale.domain(xDomain);

		svg.style('font-family', 'var(--font-primary)');

		// Y axis (labels)
		const yAxis = svg.append('g').call(d3.axisLeft(yScale).tickSize(0));

		yAxis
			.selectAll('text')
			.style('font-family', 'var(--font-primary)')
			.style('font-weight', 700)
			.style('font-size', '18px')
			.style('line-height', '28px')
			.attr('dx', '-10px');

		yAxis.selectAll('.domain').attr('stroke', 'none');

		// X axis (unit)
		const xAxis = svg
			.append('g')
			.attr('transform', `translate(0, ${height + 10})`)
			.call(
				d3
					.axisBottom(xScale)
					.ticks(dataPoints.some((d) => d.mainValue > 0) ? 5 : 3)
					.tickFormat((d) => formatToDecimals(Number(d), 0))
					.tickSize(0),
			);

		xAxis.selectAll('.domain').attr('stroke', '#4A5968');

		xAxis
			.selectAll('text')
			.style('font-family', 'var(--font-primary)')
			.style('font-weight', 400)
			.style('font-size', '16px')
			.style('line-height', '24px')
			.attr('dy', '40px');

		// Bars
		const minBarWidth = 5;

		const bars = svg
			.selectAll('rect')
			.data(dataPoints)
			.enter()
			.append('rect')
			.attr('y', (d) => yScale(d.label)!)
			.attr('x', 0)
			.attr('height', barHeight)
			.attr('width', (d) => {
				if (hasAnimated) {
					const w = xScale(d.mainValue);
					return d.mainValue === 0 ? minBarWidth : w < minBarWidth ? minBarWidth : w;
				}
				return 0;
			})
			.attr('fill', (d) => d.color)
			.attr('rx', 0)
			.attr('ry', 0);

		if (!hasAnimated) {
			bars
				.transition()
				.duration(1000)
				.delay((d, i) => i * 200)
				.attr('width', (d) => {
					const w = xScale(d.mainValue);
					return d.mainValue === 0 ? minBarWidth : w < minBarWidth ? minBarWidth : w;
				})
				.on('end', function (d, i) {
					const rect = d3.select(this);
					const width = +rect.attr('width');
					const height = +rect.attr('height');
					const radius = Math.max(d.mainValue, 0) < 12 ? Math.max(d.mainValue, 0) : 12;
					const y = +rect.attr('y');
					const w = Math.max(width - radius, 0);
					const path = `
				M0,${y}
				h${w}
				a${radius},${radius} 0 0 1 ${radius},${radius}
				v${height - 2 * radius}
				a${radius},${radius} 0 0 1 -${radius},${radius}
				h-${w}
				z
		`;
					const parentNode = rect.node()?.parentNode as Element | null;
					if (parentNode) {
						const parent = d3.select(parentNode);
						parent.append('path').attr('d', path).attr('fill', rect.attr('fill'));
						parent
							.append('text')
							.text(formatToDecimals(d.mainValue) + ' €')
							.attr('x', width + 12)
							.attr('y', y + height / 2)
							.attr('dominant-baseline', 'middle')
							.attr('text-anchor', 'start')
							.style('font-family', 'var(--font-primary)')
							.style('font-weight', 700)
							.style('font-size', '18px')
							.style('line-height', '28px')
							.style('fill', '#222');
					}
					rect.remove();
				});

			setTimeout(
				() => {
					setHasAnimated(true);
				},
				dataPoints.length * 200 + 1000,
			);
		} else {
			// Se già animato, mostra subito path e testo
			bars.each(function (d) {
				const rect = d3.select(this);
				const width = +rect.attr('width');
				const height = +rect.attr('height');
				const radius = Math.max(d.mainValue, 0) < 12 ? Math.max(d.mainValue, 0) : 12;
				const y = +rect.attr('y');
				const w = Math.max(width - radius, 0);
				const path = `
				M0,${y}
				h${w}
				a${radius},${radius} 0 0 1 ${radius},${radius}
				v${height - 2 * radius}
				a${radius},${radius} 0 0 1 -${radius},${radius}
				h-${w}
				z
		`;
				const parentNode = rect.node()?.parentNode as Element | null;
				if (parentNode) {
					const parent = d3.select(parentNode);
					parent.append('path').attr('d', path).attr('fill', rect.attr('fill'));
					parent
						.append('text')
						.text(formatToDecimals(d.mainValue) + ' €')
						.attr('x', width + 12)
						.attr('y', y + height / 2)
						.attr('dominant-baseline', 'middle')
						.attr('text-anchor', 'start')
						.style('font-family', 'var(--font-primary)')
						.style('font-weight', 700)
						.style('font-size', '18px')
						.style('line-height', '28px')
						.style('fill', '#222');
				}
				rect.remove();
			});
		}
	}, [data, containerWidth, hasAnimated]);

	return (
		<>
			<div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
				<svg ref={svgRef} style={{ display: 'block', width: '100%' }} />
			</div>
		</>
	);
};

export default HorizontalBarGraph;
